/**
 * Error Reporting and Debug Utility
 *
 * This utility provides enhanced error reporting and debugging for frontend components,
 * focusing on authentication and session state tracking.
 */

import * as Sentry from '@sentry/react';
import { debugConfig, type LogEntry } from './debug-config';
import { safeRandomUUID } from './utils';

// Store logs in memory for retrieval (legacy support)
const debugLogs: string[] = [];
const MAX_LOGS = 100;

// Log levels
export enum LogLevel {
  INFO = 'INFO',
  DEBUG = 'DEBUG',
  WARN = 'WARNING',
  ERROR = 'ERROR',
  CRITICAL = 'CRITICAL',
}

// Options for logging
interface LogOptions {
  component?: string;
  action?: string;
  data?: any;
  timestamp?: boolean;
}

// Performance tracking
const performanceMetrics = new Map<
  string,
  { start: number; end?: number; duration?: number }
>();

/**
 * Log a message with enhanced context
 */
export function logWithContext(
  level: LogLevel,
  message: string,
  options: LogOptions = {}
) {
  const {
    component = 'Unknown',
    action = 'Unknown',
    data,
    timestamp = true,
  } = options;

  const time = timestamp ? new Date().toISOString() : '';
  const logPrefix = `[${level}][${component}][${action}]${timestamp ? `[${time}]` : ''}`;

  // Format the message
  const dataString = data
    ? ` - ${JSON.stringify(data, getCircularReplacer())}`
    : '';
  const fullMessage = `${logPrefix}: ${message}${dataString}`;

  // Store log in memory (legacy)
  debugLogs.unshift(fullMessage);

  // Trim logs if exceeding max
  if (debugLogs.length > MAX_LOGS) {
    debugLogs.pop();
  }

  // Map old LogLevel to new debug-config levels
  const levelMap = {
    [LogLevel.DEBUG]: 'debug',
    [LogLevel.INFO]: 'info',
    [LogLevel.WARN]: 'warn',
    [LogLevel.ERROR]: 'error',
    [LogLevel.CRITICAL]: 'error',
  };

  const debugLevel = levelMap[level] || 'info';

  // Add to new debug system
  debugConfig.addLog({
    level: debugLevel as any,
    category: getCategoryFromAction(action),
    message,
    data,
    componentName: component,
    stackTrace:
      level === LogLevel.ERROR || level === LogLevel.CRITICAL
        ? new Error().stack
        : undefined,
  });

  // Output to console with appropriate styling
  if (debugConfig.isEnabled()) {
    switch (level) {
      case LogLevel.INFO:
        console.log(`%c${fullMessage}`, 'color: #007bff');
        break;
      case LogLevel.DEBUG:
        console.log(`%c${fullMessage}`, 'color: #6c757d');
        break;
      case LogLevel.WARN:
        console.warn(`%c${fullMessage}`, 'color: #ffc107; font-weight: bold');
        break;
      case LogLevel.ERROR:
        console.error(`%c${fullMessage}`, 'color: #dc3545; font-weight: bold');
        break;
      case LogLevel.CRITICAL:
        console.error(
          `%c${fullMessage}`,
          'color: #dc3545; background: #ffcccc; font-weight: bold; padding: 3px'
        );
        break;
      default:
        console.log(fullMessage);
    }
  }

  return fullMessage;
}

function getCategoryFromAction(action: string): LogEntry['category'] {
  if (action.includes('api')) return 'api';
  if (
    action.includes('auth') ||
    action.includes('login') ||
    action.includes('logout')
  )
    return 'state';
  if (action.includes('error')) return 'error';
  if (action.includes('render') || action.includes('mount')) return 'component';
  if (action.includes('performance')) return 'performance';
  return 'general';
}

/**
 * Helper to handle circular references in objects
 */
function getCircularReplacer() {
  const seen = new WeakSet();
  return (key: string, value: any) => {
    if (typeof value === 'object' && value !== null) {
      if (seen.has(value)) {
        return '[Circular Reference]';
      }
      seen.add(value);
    }
    return value;
  };
}

/**
 * Get all stored logs
 */
export function getLogs() {
  return debugLogs;
}

/**
 * Clear all stored logs
 */
export function clearLogs() {
  debugLogs.length = 0;
}

/**
 * Track authentication state changes
 */
export function trackAuthState(
  prevState: any,
  newState: any,
  component: string
) {
  if (!prevState || !newState) {
    logWithContext(LogLevel.INFO, 'Auth state initialized', {
      component,
      action: 'auth-init',
      data: { newState },
    });
    return;
  }

  // Track auth changes
  if (prevState.isAuthenticated !== newState.isAuthenticated) {
    const action = newState.isAuthenticated ? 'login' : 'logout';
    const level = action === 'logout' ? LogLevel.WARN : LogLevel.INFO;

    logWithContext(level, `Authentication state changed: ${action}`, {
      component,
      action,
      data: {
        before: {
          isAuthenticated: prevState.isAuthenticated,
          userId: prevState.user?.id,
          username: prevState.user?.username,
        },
        after: {
          isAuthenticated: newState.isAuthenticated,
          userId: newState.user?.id,
          username: newState.user?.username,
        },
      },
    });
  }
}

/**
 * Track network requests with detailed logging
 */
export function trackApiRequest(
  url: string,
  method: string,
  component: string,
  data?: any
) {
  logWithContext(LogLevel.DEBUG, `API Request: ${method} ${url}`, {
    component,
    action: 'api-request',
    data: { requestData: data },
  });

  // Return a timestamp for measuring duration
  return Date.now();
}

/**
 * Track network responses with detailed logging
 */
export function trackApiResponse(
  url: string,
  method: string,
  component: string,
  startTime: number,
  status: number,
  responseData?: any,
  error?: any
) {
  const duration = Date.now() - startTime;
  const logLevel = status >= 400 ? LogLevel.ERROR : LogLevel.DEBUG;

  logWithContext(
    logLevel,
    `API Response: ${method} ${url} (${status}) in ${duration}ms`,
    {
      component,
      action: 'api-response',
      data: {
        responseData: responseData,
        error: error,
        duration,
      },
    }
  );
}

/**
 * Log critical session errors
 */
export function logSessionError(error: any, component: string, action: string) {
  logWithContext(LogLevel.CRITICAL, 'Session error detected', {
    component,
    action,
    data: { error },
  });
}

/**
 * Log membership status changes
 */
export function logMembershipChange(
  leagueId: number,
  prevStatus: boolean,
  newStatus: boolean,
  userId: string
) {
  if (prevStatus !== newStatus) {
    logWithContext(
      LogLevel.INFO,
      `Membership status changed for league ${leagueId}`,
      {
        component: 'Membership',
        action: newStatus ? 'join-league' : 'leave-league',
        data: {
          userId,
          leagueId,
          previousStatus: prevStatus,
          newStatus,
        },
      }
    );
  }
}

/**
 * Check if an error is from a third-party source that we should ignore
 */
function isThirdPartyError(error: any, filename?: string, message?: string): boolean {
  // List of third-party error patterns to ignore
  const thirdPartyPatterns = [
    /metamask/i,
    /web3/i,
    /ethereum/i,
    /crypto wallet/i,
    /chrome-extension/i,
    /safari-extension/i,
    /extension:/i,
    /browser extension/i,
    /connect to wallet/i,
  ];

  // Check error message
  const errorMessage = message || error?.message || String(error || '');
  if (thirdPartyPatterns.some(pattern => pattern.test(errorMessage))) {
    console.log('🔇 [Error Filter] Ignoring third-party error:', errorMessage);
    return true;
  }

  // Check filename - ignore errors from browser extensions
  if (filename && (
    filename.includes('chrome-extension://') ||
    filename.includes('safari-extension://') ||
    filename.includes('moz-extension://')
  )) {
    console.log('🔇 [Error Filter] Ignoring browser extension error from:', filename);
    return true;
  }

  // Check error stack for third-party origins
  const stack = error?.stack || '';
  if (stack && thirdPartyPatterns.some(pattern => pattern.test(stack))) {
    console.log('🔇 [Error Filter] Ignoring third-party error from stack:', stack.substring(0, 100));
    return true;
  }

  return false;
}

/**
 * Capture unhandled errors globally
 */
export function setupGlobalErrorCatching() {
  window.addEventListener('error', (event) => {
    // Filter out third-party errors (browser extensions, wallets, etc.)
    if (isThirdPartyError(event.error, event.filename, event.message)) {
      event.preventDefault(); // Prevent console spam
      return;
    }

    // Log to console (existing behavior)
    logWithContext(LogLevel.CRITICAL, 'Unhandled error', {
      component: 'Window',
      action: 'global-error',
      data: {
        message: event.message,
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
        error: event.error,
      },
    });

    // Send to Sentry for tracking
    if (event.error) {
      Sentry.captureException(event.error, {
        contexts: {
          errorEvent: {
            message: event.message,
            filename: event.filename,
            lineno: event.lineno,
            colno: event.colno,
          },
        },
      });
    }
  });

  window.addEventListener('unhandledrejection', (event) => {
    // Filter out third-party promise rejections
    if (isThirdPartyError(event.reason, undefined, event.reason?.message)) {
      event.preventDefault(); // Prevent console spam
      return;
    }

    // Log to console (existing behavior)
    logWithContext(LogLevel.CRITICAL, 'Unhandled promise rejection', {
      component: 'Window',
      action: 'promise-rejection',
      data: {
        reason: event.reason,
      },
    });

    // Send to Sentry for tracking
    Sentry.captureException(event.reason, {
      contexts: {
        promise: {
          type: 'unhandledRejection',
        },
      },
    });
  });
}

/**
 * Track component render performance
 */
export function trackComponentPerformance(
  componentName: string,
  phase: 'start' | 'end'
) {
  const key = `component-${componentName}`;

  if (phase === 'start') {
    performanceMetrics.set(key, { start: performance.now() });
  } else {
    const metric = performanceMetrics.get(key);
    if (metric && metric.start) {
      const duration = performance.now() - metric.start;
      metric.end = performance.now();
      metric.duration = duration;

      if (debugConfig.getConfig().logPerformance && duration > 16) {
        // Log if render takes more than 16ms
        logWithContext(
          LogLevel.WARN,
          `Slow render detected: ${componentName}`,
          {
            component: componentName,
            action: 'performance-render',
            data: { duration: `${duration.toFixed(2)}ms` },
          }
        );
      }
    }
  }
}

/**
 * Track state updates
 */
export function trackStateUpdate(
  componentName: string,
  stateName: string,
  prevValue: any,
  newValue: any
) {
  if (!debugConfig.getConfig().logStateChanges) return;

  logWithContext(LogLevel.DEBUG, `State update: ${stateName}`, {
    component: componentName,
    action: 'state-update',
    data: {
      stateName,
      prevValue,
      newValue,
      changed: JSON.stringify(prevValue) !== JSON.stringify(newValue),
    },
  });
}

/**
 * Enhanced API request tracking with request/response details
 */
export function trackEnhancedApiRequest(options: {
  url: string;
  method: string;
  component: string;
  headers?: Record<string, string>;
  body?: any;
  params?: Record<string, string>;
}) {
  const { url, method, component, headers, body, params } = options;

  if (!debugConfig.getConfig().logApiRequests) {
    return Date.now();
  }

  const requestId = safeRandomUUID();

  debugConfig.addLog({
    level: 'debug',
    category: 'api',
    message: `${method} ${url}`,
    componentName: component,
    apiEndpoint: url,
    data: {
      requestId,
      method,
      headers: headers
        ? Object.fromEntries(
            Object.entries(headers).map(([k, v]) => [
              k,
              k.toLowerCase().includes('auth') ? '[REDACTED]' : v,
            ])
          )
        : undefined,
      body,
      params,
      timestamp: new Date().toISOString(),
    },
  });

  return { startTime: Date.now(), requestId };
}

/**
 * Enhanced API response tracking
 */
export function trackEnhancedApiResponse(options: {
  url: string;
  method: string;
  component: string;
  startTime: number | { startTime: number; requestId: string };
  status: number;
  statusText?: string;
  headers?: Record<string, string>;
  responseData?: any;
  error?: any;
}) {
  const {
    url,
    method,
    component,
    startTime,
    status,
    statusText,
    headers,
    responseData,
    error,
  } = options;

  if (!debugConfig.getConfig().logApiRequests) return;

  const start = typeof startTime === 'number' ? startTime : startTime.startTime;
  const requestId =
    typeof startTime === 'object' ? startTime.requestId : undefined;
  const duration = Date.now() - start;

  debugConfig.addLog({
    level: status >= 400 ? 'error' : 'debug',
    category: 'api',
    message: `${method} ${url} - ${status} ${statusText || ''} (${duration}ms)`,
    componentName: component,
    apiEndpoint: url,
    duration,
    data: {
      requestId,
      status,
      statusText,
      headers,
      responseData: error ? undefined : responseData,
      error: error
        ? {
            message: error.message || error,
            stack: error.stack,
            response: error.response,
          }
        : undefined,
      timestamp: new Date().toISOString(),
    },
  });
}

/**
 * Export debug data for bug reports
 */
export function exportDebugData() {
  return {
    logs: getLogs(),
    debugSystemLogs: debugConfig.getLogs(),
    config: debugConfig.getConfig(),
    performanceMetrics: Array.from(performanceMetrics.entries()).map(
      ([key, value]) => ({
        key,
        ...value,
      })
    ),
    timestamp: new Date().toISOString(),
    userAgent: navigator.userAgent,
    url: window.location.href,
    viewport: {
      width: window.innerWidth,
      height: window.innerHeight,
    },
  };
}
